import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { User, Mail, Lock, Store, ArrowRight, Loader2, CreditCard, Eye, EyeOff, FileText, X } from 'lucide-react';
import { useState } from 'react';
import React from 'react';

import { api, setAuthToken } from '@/lib/api.ts';
import TurnstileWidget from '@/components/TurnstileWidget.jsx';
import TerminosCondiciones from '@/components/TerminosCondiciones.jsx';

export function RegistroComercianteForm() {
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [cedulaTipo, setCedulaTipo] = useState('V');
  const [showPassword, setShowPassword] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Bloquear scroll del body cuando el modal está abierto
  React.useEffect(() => {
    if (showTerms) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTerms]);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      nombre: '',
      email: '',
      password: '',
      tienda: '',
      cedula_numero: '',
    },
  });
  const navigate = useNavigate();

  const registro = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post('/api/auth/register/merchant', payload);
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success('Tienda creada exitosamente. Revisa tu correo para verificar.');
      if (data?.token) {
        setAuthToken(data.token);
      }
      navigate(`/verificar-correo?email=${encodeURIComponent(variables.email)}`);
    },
    onError: (error) => {
      const message = error.response?.data?.detail ?? 'No se pudo crear la tienda';
      toast.error(message);
    },
  });

  const onSubmit = (values) => {
    // Validar que aceptó términos
    if (!acceptedTerms) {
      toast.error('Debes aceptar los Términos y Condiciones para continuar');
      return;
    }
    
    // Combinar tipo de cédula + número
    const cedula = `${cedulaTipo}-${values.cedula_numero}`;
    
    // Agregar el token de Turnstile al payload
    const payload = {
      ...values,
      cedula,  // ← Enviar "V-12345678"
      turnstile_token: turnstileToken,
      terminos_aceptados: true,
      terminos_version: '1.0',
    };
    
    // Remover cedula_numero del payload (ya está en cedula)
    delete payload.cedula_numero;
    
    registro.mutate(payload);
  };

  // --- ESTILOS COMPACTOS (Sin scroll) ---
  const groupClass = "space-y-1.5"; // Espacio reducido entre Label y Input
  const labelClass = "block text-[9px] font-bold uppercase tracking-widest text-white/40 ml-1";
  const inputContainerClass = "relative group";
  // h-10 para altura más compacta, pl-10 para separar texto del icono
  const inputClass = "w-full h-10 bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all";
  // top-1/2 -translate-y-1/2 para centrado vertical perfecto
  const iconClass = "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30 group-focus-within:text-emerald-400 transition-colors";
  const errorClass = "text-[10px] text-red-400 mt-0.5 ml-1";

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      
      {/* GRID HORIZONTAL COMPACTO (gap-4) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        {/* Nombre Completo */}
        <div className={groupClass}>
          <label className={labelClass}>Nombre Completo</label>
          <div className={inputContainerClass}>
            <User className={iconClass} />
            <input
                type="text"
                placeholder="Tu nombre"
                className={inputClass}
                {...register('nombre', {
                    required: 'Requerido',
                    minLength: { value: 2, message: 'Mínimo 2 caracteres' }
                })}
            />
          </div>
          {errors.nombre && <p className={errorClass}>{errors.nombre.message}</p>}
        </div>

        {/* Nombre de la Tienda */}
        <div className={groupClass}>
          <label className={labelClass}>Nombre de tu Tienda</label>
          <div className={inputContainerClass}>
            <Store className={iconClass} />
            <input
                type="text"
                placeholder="Ej: Moda Espacial"
                className={inputClass}
                {...register('tienda', {
                    required: 'Requerido',
                    minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                })}
            />
          </div>
          {errors.tienda && <p className={errorClass}>{errors.tienda.message}</p>}
        </div>

        {/* Cédula / RIF */}
        <div className={groupClass}>
          <label className={labelClass}>Cédula / RIF</label>
          <div className="flex gap-2">
            {/* Select para tipo (J/V/E) */}
            <select
              value={cedulaTipo}
              onChange={(e) => setCedulaTipo(e.target.value)}
              className="h-10 bg-black/40 border border-white/10 rounded-xl px-3 text-sm text-white focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all"
            >
              <option value="V">V</option>
              <option value="J">J</option>
              <option value="E">E</option>
            </select>
            
            {/* Input para número */}
            <div className="flex-1 relative group">
              <CreditCard className={iconClass} />
              <input
                type="text"
                placeholder="12345678"
                className={inputClass}
                {...register('cedula_numero', {
                  required: 'Requerido',
                  pattern: { value: /^[0-9]+$/, message: 'Solo números' }
                })}
              />
            </div>
          </div>
          {errors.cedula_numero && <p className={errorClass}>{errors.cedula_numero.message}</p>}
        </div>

        {/* Email (Ocupa 2 columnas) */}
        <div className={`${groupClass} md:col-span-2`}>
          <label className={labelClass}>Correo Electrónico</label>
          <div className={inputContainerClass}>
            <Mail className={iconClass} />
            <input
                type="email"
                placeholder="tienda@ejemplo.com"
                className={inputClass}
                {...register('email', {
                    required: 'Requerido',
                    pattern: { value: /^\S+@\S+$/i, message: 'Email inválido' }
                })}
            />
          </div>
          {errors.email && <p className={errorClass}>{errors.email.message}</p>}
        </div>

        {/* Contraseña (Ocupa 2 columnas) */}
        <div className={`${groupClass} md:col-span-2`}>
          <label className={labelClass}>Contraseña</label>
          <div className={inputContainerClass}>
            <Lock className={iconClass} />
            <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                className={inputClass}
                {...register('password', {
                    required: 'Requerido',
                    minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                    pattern: { value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, message: 'Requiere mayúscula, minúscula y número' }
                })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors focus:outline-none"
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.password && <p className={errorClass}>{errors.password.message}</p>}
        </div>
      </div>

      {/* Banner Informativo (Glass) - Compacto */}
      <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/10 p-3 flex items-start gap-2.5">
        <div className="p-1 bg-emerald-500/20 rounded-full mt-0.5 flex-shrink-0">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
        </div>
        <div>
            <h4 className="text-[10px] font-bold text-emerald-400 uppercase tracking-wide mb-0.5">Prueba Gratuita Activada</h4>
            <p className="text-[10px] text-emerald-200/60 leading-relaxed">
                15 días de acceso total sin tarjeta de crédito. Cancela cuando quieras.
            </p>
        </div>
      </div>

      {/* Widget de Turnstile (Captcha) */}
      <TurnstileWidget
        onVerify={(token) => setTurnstileToken(token)}
        onError={() => setTurnstileToken(null)}
        onExpire={() => setTurnstileToken(null)}
      />

      {/* Checkbox de Términos y Condiciones */}
      <div className="rounded-xl bg-white/5 border border-white/10 p-4">
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="w-5 h-5 rounded border-2 border-white/20 bg-black/40 checked:bg-emerald-500 checked:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all cursor-pointer"
            />
          </div>
          <div className="flex-1">
            <p className="text-xs text-white/70 leading-relaxed">
              Acepto los{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowTerms(true);
                }}
                className="text-emerald-400 hover:text-emerald-300 underline font-semibold transition-colors inline-flex items-center gap-1"
              >
                <FileText className="h-3 w-3" />
                Términos y Condiciones
              </button>
              {' '}de uso de TiendasYA
            </p>
            <p className="text-[10px] text-white/40 mt-1">
              Comprendo que TiendasYA es un proveedor de software y soy responsable de mis operaciones comerciales.
            </p>
          </div>
        </label>
      </div>

      {/* Botón de Acción - Más compacto */}
      <button
        type="submit"
        disabled={registro.isPending || !acceptedTerms}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold uppercase tracking-widest text-xs shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed border border-white/10"
      >
        {registro.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Creando Infraestructura...
          </>
        ) : (
          <>
            Lanzar Tienda <ArrowRight className="h-4 w-4" />
          </>
        )}
      </button>

      {!acceptedTerms && (
        <p className="text-[10px] text-center text-amber-400/60 flex items-center justify-center gap-1">
          <FileText className="h-3 w-3" />
          Debes aceptar los términos para continuar
        </p>
      )}

      {/* Modal de Términos y Condiciones */}
      {showTerms && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-hidden"
          onClick={() => setShowTerms(false)}
        >
          <div 
            className="bg-[#1a1f2e] rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col shadow-2xl border border-white/5"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del Modal - Fijo */}
            <div className="flex items-center justify-between p-6 border-b border-white/5 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <FileText className="h-5 w-5 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Términos y Condiciones</h2>
              </div>
              <button
                onClick={() => setShowTerms(false)}
                className="p-2 hover:bg-white/5 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white/40 hover:text-white/60" />
              </button>
            </div>
            
            {/* Contenido del Modal - Scrolleable */}
            <div className="overflow-y-auto flex-1 p-6">
              <TerminosCondiciones />
            </div>
            
            {/* Footer del Modal - Fijo */}
            <div className="p-6 border-t border-white/5 flex gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setAcceptedTerms(true);
                  setShowTerms(false);
                  toast.success('Términos aceptados');
                }}
                className="flex-1 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition-all shadow-lg shadow-emerald-600/20"
              >
                Aceptar y Continuar
              </button>
              <button
                onClick={() => setShowTerms(false)}
                className="px-8 py-3.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white font-semibold text-sm transition-all"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

    </form>
  );
}